// Minimal hast node types — avoids adding `hast` as a direct dependency.
type HastText = { type: 'text'; value: string };
type HastElement = {
  type: 'element';
  tagName: string;
  properties: Record<string, unknown>;
  children: Array<HastText | HastElement>;
};
type HastParent = { children: Array<HastText | HastElement> };

/**
 * Rehype plugin that wraps `/skill-name` tokens in user messages with a
 * styled span so they render with the same accent color used in the editor.
 *
 * Only highlights tokens whose slug matches a known skill from the workspace.
 */
export function rehypeSkillHighlight(slugs: Set<string>) {
  return (tree: HastParent) => {
    visitTextNodes(tree, (node, index, parent) => {
      if (parent == null || index == null) return;

      const regex = /\/[\w-]+/g;
      let match: RegExpExecArray | null;
      const segments: Array<HastText | HastElement> = [];
      let lastIndex = 0;

      // eslint-disable-next-line no-cond-assign
      while ((match = regex.exec(node.value)) !== null) {
        const slug = match[0].slice(1);
        if (!slugs.has(slug)) continue;

        // Text before the match
        if (match.index > lastIndex) {
          segments.push({ type: 'text', value: node.value.slice(lastIndex, match.index) });
        }

        // Highlighted span
        segments.push({
          type: 'element',
          tagName: 'span',
          properties: { className: ['skill-command-highlight'] },
          children: [{ type: 'text', value: match[0] }],
        });

        lastIndex = match.index + match[0].length;
      }

      if (segments.length === 0) return;

      // Remaining text after last match
      if (lastIndex < node.value.length) {
        segments.push({ type: 'text', value: node.value.slice(lastIndex) });
      }

      // Replace the original text node with the segments
      parent.children.splice(index, 1, ...segments);
    });
  };
}

/** Walk all text nodes in the hast tree */
function visitTextNodes(node: HastParent, callback: (node: HastText, index: number, parent: HastParent) => void) {
  for (let index = 0; index < node.children.length; index++) {
    const child = node.children[index];
    if (child.type === 'text') {
      const originalLength = node.children.length;
      callback(child, index, node);
      // Adjust index if splice added nodes
      const delta = node.children.length - originalLength;
      index += delta;
    } else if (child.type === 'element') {
      visitTextNodes(child, callback);
    }
  }
}
